import { ReactElement, useEffect, useReducer, useRef, useState } from "react";

import styles from "./index.module.css";
import { useDrawings } from "../../../state/drawings";
import { useSettings } from "../../../state/settings";
import { useTool } from "../../../state/tool";
import { Drawing } from "../../../lib/drawing";
import { flip, flood } from "../../../lib/drawing/paint";
import {
  crosshairPath,
  floodPath,
  straightLinePath,
} from "../../../lib/drawing/path";

function reducer(
  state: {
    currFill?: boolean;
    isActive?: boolean;
  },
  action: {
    type: "start" | "continue" | "end";
    fill?: boolean;
  }
) {
  switch (action.type) {
    case "start":
      return {
        currFill: action.fill,
        isActive: true,
      };
    case "continue":
      return {
        currFill: state.currFill,
        isActive: true,
      };
    case "end":
      return {};
  }
}

export default function Sensor(props: { drawing: Drawing }): ReactElement {
  const { settings } = useSettings();
  const { update } = useDrawings();
  const [stroke, updateStroke] = useReducer(reducer, {});

  // Runs whenever the hovered pixel changes, and updates the hover state
  // appropriately.
  const { tool, toolShadow, setToolShadow } = useTool();
  const [hoveredPixel, setHoveredPixel] = useState(-1);
  const [lineOrigin, setLineOrigin] = useState(-1);
  useEffect(() => {
    if (!settings.toolShadow) return;

    if (hoveredPixel < 0) {
      setToolShadow({});
      return;
    }

    switch (tool) {
      case "brush":
        setToolShadow(crosshairPath(hoveredPixel));
        break;
      case "flood":
        setToolShadow(floodPath(props.drawing, hoveredPixel));
        break;
      case "line":
        setToolShadow(
          stroke.isActive
            ? straightLinePath(props.drawing, lineOrigin, hoveredPixel)
            : { [hoveredPixel]: true }
        );
        break;
    }
  }, [
    hoveredPixel,
    lineOrigin,
    props.drawing,
    setToolShadow,
    settings.toolShadow,
    stroke.isActive,
    tool,
  ]);

  // Reset state whenever the tool changes.
  useEffect(() => {
    updateStroke({ type: "end" });
    setLineOrigin(-1);
  }, [tool]);

  function startStroke(idx: number): void {
    updateStroke({ type: "start", fill: !props.drawing.pixels[idx] });

    switch (tool) {
      case "brush":
        update(props.drawing, flip(props.drawing, [idx]));
        break;
      case "flood":
        update(props.drawing, flood(props.drawing, idx));
        break;
      case "line":
        setLineOrigin(idx);
        break;
    }
  }

  function movePointer(idx: number): void {
    // Update the hover state while filtering out irrelevant movements.
    if (idx === hoveredPixel) return;
    if (stroke.isActive && tool === "flood") return;
    setHoveredPixel(idx);

    // Continue the stroke, if needed.
    if (tool === "flood" || tool === "line") return;
    if (stroke.isActive && stroke.currFill === !props.drawing.pixels[idx]) {
      updateStroke({ type: "continue" });
      update(props.drawing, flip(props.drawing, [idx], false));
    }
  }

  useEffect(() => {
    function endStroke(e?: Event): void {
      if (!stroke.isActive) return;

      if (
        tool === "line" &&
        lineOrigin !== hoveredPixel &&
        lineOrigin > -1 &&
        hoveredPixel > -1
      ) {
        update(
          props.drawing,
          flip(
            props.drawing,
            Object.keys(
              straightLinePath(props.drawing, lineOrigin, hoveredPixel)
            ).map((idx) => parseInt(idx))
          )
        );
        setHoveredPixel(-1);
      }

      updateStroke({ type: "end" });
      setLineOrigin(-1);
      if (e instanceof MouseEvent) return;
      setHoveredPixel(-1);
    }

    window.addEventListener("mouseup", endStroke);
    window.addEventListener("touchend", endStroke);
    window.addEventListener("touchcancel", endStroke);
    return () => {
      window.removeEventListener("mouseup", endStroke);
      window.removeEventListener("touchend", endStroke);
      window.removeEventListener("touchcancel", endStroke);
    };
  });

  function handleMouseLeave() {
    if (tool === "line" && stroke.isActive) return;
    setHoveredPixel(-1);
  }

  // We can't use React's synthetic events for touch events, because there's no
  // way to make them non-passive. Non-passive events can be canceled by calling
  // event.preventDefault(). This is needed to stop touch events on the table
  // from scrolling the page.
  //
  // Instead, we keep a ref to the table element here, so that we can force the
  // native touchstart event to be non-passive.
  const sensorEl = useRef<HTMLTableElement>(null);
  useEffect(() => {
    function onTouchStart(e: TouchEvent): void {
      e.preventDefault();
      const pixel = getTouchedPixel(e);
      if (pixel === null) return;
      startStroke(pixel);
    }

    const curr = sensorEl.current;
    if (!curr) return;
    curr.addEventListener("touchstart", onTouchStart, {
      passive: false,
    });
    return () => curr.removeEventListener("touchstart", onTouchStart);
  });

  function handleTouchMove(e: React.TouchEvent): void {
    const pixel = getTouchedPixel(e);
    if (pixel === null) return;
    movePointer(pixel);
  }

  function getTouchedPixel(e: React.TouchEvent | TouchEvent): number | null {
    const touch = e.changedTouches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!(target instanceof HTMLElement)) return null;
    const pixel = target.dataset.pixel;
    if (!pixel) return null;
    return parseInt(pixel);
  }

  function pixelStyle(idx: number): string {
    let className = `${styles.pixel}`;
    if (props.drawing.pixels[idx]) {
      className += ` ${styles.filled}`;
    }
    if (toolShadow[idx]) {
      switch (tool) {
        case "brush":
          className += ` ${styles.brushShadow}`;
          break;
        case "flood":
          className += ` ${styles.floodShadow}`;
          break;
        case "line":
          className += ` ${styles.lineShadow}`;
          break;
      }
    }
    return className;
  }

  const sensor = [];
  for (let row = 0; row < Drawing.side; row++) {
    const rows = [];
    for (let col = 0; col < Drawing.side; col++) {
      const idx = row * Drawing.side + col;
      rows.push(
        <td
          key={idx}
          data-pixel={idx}
          onMouseDown={() => startStroke(idx)}
          onMouseEnter={() => movePointer(idx)}
          className={pixelStyle(idx)}
        />
      );
    }
    sensor.push(<tr key={row}>{rows}</tr>);
  }

  return (
    <table
      ref={sensorEl}
      onTouchMove={handleTouchMove}
      onMouseLeave={handleMouseLeave}
      className={`${styles.sensor} ${
        settings.gridLines ? styles.showGridLines : ""
      }`}
    >
      <tbody>{sensor}</tbody>
    </table>
  );
}
